/**
 * Kickard PHASE 4.0.4 — Ranked API client.
 * Small helpers around the public/queue endpoints. All fetches route
 * through REACT_APP_BACKEND_URL and gracefully surface disabled/no-season
 * codes for the UI to render safe fallback states.
 *
 * PHASE 5.0B — Transient-fetch resilience.
 * Read-only Ranked GET helpers (availability / me / leaderboard /
 * queue-status / result) now retry transient browser-level fetch
 * failures (Safari TypeError "Load failed", iOS ITP cold starts,
 * flaky LTE wakes, DNS flap, extension interference) a bounded
 * number of times with short exponential backoff before giving up.
 * HTTP responses (any status code) are NEVER retried — those surface
 * verbatim so real backend state (disabled, no-season, 404, 5xx)
 * remains visible exactly as before. Mutations (queue join / cancel)
 * are intentionally NOT retried.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function _json(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = body?.detail?.code || body?.code || `http_${res.status}`;
    return { ok: false, status: res.status, code, body };
  }
  return { ok: true, status: res.status, ...body };
}

/**
 * Bounded GET with transient-network retry.
 *   • Up to 3 attempts total (1 + 2 retries).
 *   • Exponential backoff: ~250ms, ~500ms.
 *   • Retries ONLY on exceptions thrown by fetch itself (network/CORS
 *     level — e.g. Safari TypeError "Load failed"). Any HTTP response
 *     (2xx/4xx/5xx) is returned immediately and never retried here.
 */
async function _getWithRetry(url, { credentials } = {}) {
  const RETRIES = 2;
  const BASE = 250;
  let lastErr = null;
  for (let attempt = 0; attempt <= RETRIES; attempt++) {
    try {
      const res = credentials
        ? await fetch(url, { credentials })
        : await fetch(url);
      return await _json(res);
    } catch (e) {
      lastErr = e;
      if (attempt < RETRIES) {
        await new Promise((r) => setTimeout(r, BASE * Math.pow(2, attempt)));
        continue;
      }
    }
  }
  return { ok: false, code: 'network_error', error: String(lastErr?.message || lastErr || 'network_error') };
}

export async function getRankedAvailability() {
  return _getWithRetry(`${BACKEND}/api/ranked/availability`, { credentials: 'omit' });
}

export async function getMyRankedState(guestId) {
  return _getWithRetry(
    `${BACKEND}/api/ranked/me?guestId=${encodeURIComponent(guestId || '')}`,
  );
}

export async function joinRankedQueue({ guestId, displayName, profileSlug, sessionId } = {}) {
  try {
    const res = await fetch(`${BACKEND}/api/ranked/queue/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId, displayName, profileSlug, sessionId }),
    });
    return await _json(res);
  } catch (e) { return { ok: false, code: 'network_error' }; }
}

export async function getRankedQueueStatus(guestId) {
  return _getWithRetry(
    `${BACKEND}/api/ranked/queue/status?guestId=${encodeURIComponent(guestId || '')}`,
  );
}

export async function cancelRankedQueue(guestId) {
  try {
    const res = await fetch(`${BACKEND}/api/ranked/queue/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId }),
    });
    return await _json(res);
  } catch (e) { return { ok: false, code: 'network_error' }; }
}

export async function getRankedResult(matchId, guestId) {
  const url = `${BACKEND}/api/ranked/result/${encodeURIComponent(matchId)}?guestId=${encodeURIComponent(guestId || '')}`;
  return _getWithRetry(url);
}

// Phase 4.1.0 — Public ranked leaderboard.
export async function getRankedLeaderboard({ seasonId, limit, guestId } = {}) {
  const params = new URLSearchParams();
  if (seasonId) params.set('seasonId', seasonId);
  if (limit !== undefined && limit !== null) params.set('limit', String(limit));
  if (guestId) params.set('guestId', guestId);
  const qs = params.toString();
  const url = `${BACKEND}/api/ranked/leaderboard${qs ? `?${qs}` : ''}`;
  return _getWithRetry(url, { credentials: 'omit' });
}

// Pure formatters (unit-testable, no DOM).
export function formatSRDelta(delta) {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return '';
  const n = Number(delta);
  if (n > 0) return `+${n} SR`;
  return `${n} SR`;   // negative already includes '-'
}

export function formatTierChange(beforeTier, afterTier) {
  if (!beforeTier || !afterTier || beforeTier === afterTier) return null;
  const order = { unranked: 0, bronze: 1, silver: 2, gold: 3, platinum: 4,
                  diamond: 5, master: 6, champion: 7, legend: 8 };
  const up = (order[afterTier] || 0) > (order[beforeTier] || 0);
  const label = afterTier.charAt(0).toUpperCase() + afterTier.slice(1);
  return up ? `Promoted to ${label}` : `Demoted to ${label}`;
}
